/**
 * Homepage configuration — seasonal windows, campaign slot, evergreen picks.
 * Latest long-form and DT teasers are computed from content metadata (not hard-coded).
 */

export const HOME_SEO = {
  title: 'தெய்வீக ஓசை | தமிழ் பக்திப் பாடல்கள், ஸ்துதிகள் & ஆன்மீக தகவல்கள்',
  description:
    'தமிழ் பக்திப் பாடல்கள், ஸ்துதிகள், மந்திரங்கள், வரிகள் & பொருள், இலவச PDFகள் மற்றும் “தெய்வீகம் தெரியுமா?” ஆன்மீக தகவல்கள் — Deiveega Oosai.',
} as const

/** Optional giveaway / campaign card — enable without redeploying layout code. */
export interface HomeCampaign {
  active: boolean
  label: string
  headline: string
  text: string
  /** ISO datetime with offset */
  start: string
  end: string
  ctaLabel: string
  ctaUrl: string
}

export const HOME_CAMPAIGN: HomeCampaign = {
  active: true,
  label: 'நவராத்திரி Giveaway',
  headline: 'நவராத்திரி Giveaway — பங்கேற்க',
  text: 'அக்டோபர் 12 • 16 • 19 — போட்டி தேதிகள், வீடியோ மற்றும் Form விவரங்களுக்கு இங்கே வருங்கள்.',
  start: '2026-10-08T00:00:00+05:30',
  end: '2026-10-20T07:00:00+05:30',
  ctaLabel: '🎁 நவராத்திரி பரிசுப் போட்டி',
  ctaUrl: '/navratri/',
}

export function isCampaignLive(campaign: HomeCampaign = HOME_CAMPAIGN, now = Date.now()) {
  if (!campaign.active || !campaign.ctaUrl.trim()) return false
  const start = Date.parse(campaign.start)
  const end = Date.parse(campaign.end)
  if (Number.isNaN(start) || Number.isNaN(end)) return false
  return now >= start && now <= end
}

/** Active Navratri 2026 seasonal hero + collection (window includes soft launch before 11 Oct). */
export const NAVRATRI_2026 = {
  id: 'navratri-2026',
  tag: 'navratri-2026',
  eyebrow: 'நவராத்திரி 2026 • அக்டோபர் 11–20',
  titleTa: 'நவராத்திரி பக்தி சிறப்பு',
  supportTa:
    'துர்கை, லட்சுமி, சரஸ்வதி அருளை நினைத்து பாடல்கள், ஸ்துதிகள், போற்றிகள் மற்றும் இலவச PDFகளை ஒரே இடத்தில் கேட்டு படியுங்கள்.',
  primaryCtaLabel: 'நவராத்திரி சிறப்பு பார்க்க',
  primaryCtaHash: '#navratri-collection',
  secondaryCtaLabel: 'YouTube-ல் கேட்க',
  /** Soft start so homepage shows Navratri from 08 Oct; ends after Vijayadashami. */
  seasonStart: '2026-10-08T00:00:00+05:30',
  seasonEnd: '2026-10-20T07:00:00+05:30',
  contentIds: [
    'narayani-stuti',
    'aigiri-nandini',
    'amman-potri-108',
    'abirami-16-blessings',
    'mahalakshmi-ashtakam',
    'kanakadhara-stotram',
  ],
} as const

export function isNavratriSeasonActive(now = Date.now()) {
  const start = Date.parse(NAVRATRI_2026.seasonStart)
  const end = Date.parse(NAVRATRI_2026.seasonEnd)
  return now >= start && now <= end
}

/** Manual evergreen favorites — never labeled “Latest”. */
export const EVERGREEN_CONTENT_IDS = [
  'lingashtakam',
  'aigiri-nandini',
  'uruvai-aruvai',
  'muthai-tharu',
  'ayyappan-song',
  'mahalakshmi-song',
] as const

/** Featured PDF downloads for Navratri window (goddess-first + one fresh). */
export const HOME_FEATURED_PDF_IDS = [
  'mahalakshmi-ashtakam',
  'abirami-16-blessings',
  'kanakadhara-stotram',
  'ganesha-pancharatnam',
] as const

/**
 * YouTube original publish dates (YYYY-MM-DD).
 * Latest grid sorts by this field — not website insertion order.
 */
export const YOUTUBE_PUBLISHED_AT: Record<string, string> = {
  // Top-12 / audit Latest order
  'ganesha-pancharatnam': '2026-10-05',
  'kanneerodu-vandhene': '2026-10-02',
  'runa-vimochana-ganapati': '2026-09-28',
  'govinda-namavali': '2026-09-25',
  'vaidyanatha-ashtakam': '2026-09-21',
  'sri-venkatesa-stotram': '2026-09-18',
  // Other handoff long-form (older than Latest top-6)
  'kanakadhara-stotram': '2026-09-14',
  'mahalakshmi-ashtakam': '2026-09-11',
  'madhurashtakam': '2026-09-09',
  'abirami-16-blessings': '2026-09-07',
  'vishnu-108-names': '2026-09-04',
  'vinayagar-potri-108': '2026-09-01',
  // Phase-1 evergreen library (older)
  lingashtakam: '2025-08-12',
  'aigiri-nandini': '2025-07-20',
  'aintu-karathanai': '2025-06-15',
  'uruvai-aruvai': '2025-05-10',
  'muthai-tharu': '2025-04-18',
  'amman-potri-108': '2025-03-22',
  'vinayagar-mantras': '2025-02-14',
  'ayyappan-song': '2025-01-20',
  'mahalakshmi-song': '2024-12-08',
}

export const LATEST_LONGFORM_TYPES = [
  'original-song',
  'traditional-stuti',
  'mantra',
  'potri',
] as const

export function isWithinDays(isoDate: string, days: number, now = Date.now()) {
  const t = Date.parse(`${isoDate}T12:00:00+05:30`)
  if (Number.isNaN(t)) return false
  return now - t <= days * 24 * 60 * 60 * 1000 && now >= t - 24 * 60 * 60 * 1000
}

export function formatYtDateTa(isoDate: string) {
  const d = new Date(`${isoDate}T12:00:00+05:30`)
  if (Number.isNaN(d.getTime())) return isoDate
  return d.toLocaleDateString('ta-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}
